import { useEffect, useRef, useState } from "react";
import { animateScroll } from "react-scroll";
import styled from "styled-components";
import {
  BASIC_DARK_GRAY,
  BASIC_LIGHT_GRAY,
  BASIC_WHITE,
} from "../../../constants/colors";
import { Category } from "../../../ducks/menu";
import { MenuCategoryItem } from "../../molecules/menu/MenuCategoryItem";

type Props = {
  categoryRefs: React.MutableRefObject<{
    [id: string]: { el: HTMLDivElement | null; data: Category };
  }>;
  categories: {
    id: number;
    name: string;
    text: string;
  }[];
  showAppCouponHeader: boolean;
};

export const MenuCategoriesList: React.FC<Props> = ({
  categories,
  categoryRefs,
  showAppCouponHeader,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(
    categories[0]?.id ?? 0,
  );
  const [isObserverActive, setIsObserverActive] = useState(true);

  const handleScrollToCategory = (id: number) => {
    setIsObserverActive(false);
    const categoryBarElement = categoryRefs.current[id.toString()]?.el;
    const appHeader = document.getElementById("app-header");
    if (categoryBarElement && appHeader && ref.current) {
      const containerOffset = ref.current.offsetHeight;
      const totalOffsetTop = appHeader.offsetHeight + containerOffset + 30;
      const elementPosition =
        categoryBarElement.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - totalOffsetTop;
      const isAtBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight;
      const shouldScroll = isAtBottom ? window.scrollY >= offsetPosition : true;

      if (shouldScroll) {
        animateScroll.scrollTo(offsetPosition, {
          duration: 500,
          smooth: true,
        });
      }

      setTimeout(() => {
        setIsObserverActive(true);
      }, 510);
    }
  };

  useEffect(() => {
    setSelectedCategoryId(categories[0]?.id ?? 0);
  }, [categories]);

  useEffect(() => {
    if (!isObserverActive) return;
    if (!ref.current) return;

    const appHeader = document.getElementById("app-header");
    if (!appHeader) return;

    const categoryOffset = ref.current.offsetHeight;
    const totalOffsetTop = appHeader.offsetHeight + categoryOffset;

    const handleScroll = () => {
      const entries = Object.values(categoryRefs.current)
        .map((ref) => {
          if (!ref.el) return null;
          const rect = ref.el.getBoundingClientRect();
          return {
            target: ref.el,
            isIntersecting: rect.top >= 0 && rect.bottom <= window.innerHeight,
            intersectionRect: rect,
          };
        })
        .filter(Boolean);

      const interesectingOnly = entries.filter(
        (entry) => entry?.isIntersecting,
      );

      if (interesectingOnly.length > 0) {
        const shouldSelect = interesectingOnly.find(
          (e) =>
            e &&
            e.intersectionRect.y > totalOffsetTop &&
            e.intersectionRect.y < totalOffsetTop + categoryOffset,
        );

        if (shouldSelect) {
          const id = shouldSelect.target.getAttribute("data-id");
          setSelectedCategoryId(categoryRefs.current[Number(id)]?.data.id);
        }

        return;
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isObserverActive]);

  return (
    <MenuCategoriesListContainer ref={ref} $hasAppCoupon={showAppCouponHeader}>
      <MenuCategoryListWrapper>
        <MenuCategoryWrapper>
          {categories.map((category) => (
            <MenuCategoryItem
              id={category.id}
              key={category.id}
              categoryName={category.name}
              selected={selectedCategoryId === category.id}
              onClick={() => {
                setSelectedCategoryId(category.id);
                handleScrollToCategory(category.id);
              }}
            />
          ))}
        </MenuCategoryWrapper>

        {categoryRefs && (
          <MenuCategoryText>
            {categoryRefs?.current[selectedCategoryId]?.data?.text}
          </MenuCategoryText>
        )}
      </MenuCategoryListWrapper>
    </MenuCategoriesListContainer>
  );
};

const MenuCategoriesListContainer = styled.div<{ $hasAppCoupon: boolean }>`
  position: sticky;
  top: ${({ $hasAppCoupon }) => ($hasAppCoupon ? "100px" : "50px")};
  z-index: 1;
  padding: 0;
  margin: 0;
  display: flex;
  justify-content: center;
  align-self: center;
  width: 100vw;
  min-width: 375px;
  background-color: ${BASIC_WHITE};
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0px 20px 20px 0px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(66, 66, 66, 0.01);
`;

const MenuCategoryListWrapper = styled.div`
  max-width: 768px;
  width: 100%;
`;

const MenuCategoryWrapper = styled.div`
  margin: 0;
  padding: 0;
  //border-bottom: 1px solid ${BASIC_LIGHT_GRAY};
  overflow-x: auto;
`;

const MenuCategoryText = styled.div`
  margin: 10px;
  text-align: left;
  font-size: 12px;
  color: ${BASIC_DARK_GRAY};
  line-height: 2em;
  word-wrap: break-word;
  white-space: normal;
`;
